/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { Injectable } from '@angular/core';
import { SQL_RESPONSE_PAGE_SIZE } from '@app/common/constants';
import { Confirm } from '@app/common/modules/gmc-confirm/confirm.service';
import { handleLoading } from '@app/common/rxjs-operators';
import { completedClusterAction } from '@app/common/rxjs-operators/completed-cluster-action';
import { extractError } from '@app/common/utils';
import {
  caches,
  clusterIdsOfLoadedCaches,
  currentCluster,
  currentClusterNodes,
  dashboardGroup,
  dashboardGroupByName,
  selectUrl,
  updateDashboardGroup,
} from '@app/core/ngrx';
import { isCluster2xSelector } from '@app/core/ngrx/cluster.selectors';
import { ClusterActionService } from '@app/core/services';
import { DashboardApiService } from '@app/core/services/dashboard.service';
import { GmcErrorCodes } from '@app/core/types';
import { ExportQueryService } from '@app/sql/export-query.service';
import { interceptActionTaskErrors } from '@common/utils/task-interceptor';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ROUTER_NAVIGATED, ROUTER_NAVIGATION } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import {
  ActionName,
  ClusterAction,
  ClusterActionCompletedResponse,
  ClusterActionFailedResponse,
  ResponseStatus,
} from '@shared/types/cluster-actions';
import { ExecuteQueryResponseType, QueryExportMessage, QueryType } from '@shared/types/queries';
import { isEqual } from 'lodash-es';
import { EMPTY, combineLatest, merge, of, throwError } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  pairwise,
  pluck,
  startWith,
  switchMap,
  take,
  takeUntil,
  toArray,
  withLatestFrom,
} from 'rxjs/operators';
import {
  QUERIES_GROUP_NAME,
  extractStatementsFromQuery,
  isSqlQuery,
  prepareExecuteScanQueryArgs,
  prepareExecuteSqlArgs,
  replaceActionParametersWithValues,
  replaceFrontendParametersWithValues,
} from '../domain/query';
import { QueryService } from '../query.service';
import { State } from '../types';
import {
  cancelCurrentQuery,
  cancelExportQuerySuccess,
  cancelQuery,
  cancelQueryFailure,
  cancelQuerySuccess,
  changeCurrentQueryAdvancedSettings,
  changeCurrentQueryParameters,
  clearQueryResult,
  createQuery3xSuccess,
  downloadExportQueryFailure,
  executeExportPartialQuery,
  executeExportQuery,
  executeExportQueryFailure,
  executeExportQuerySuccess,
  executePartialQuery,
  executeQuery,
  executeQuery3xFailure,
  executeQueryFailure,
  getNextPageFailure,
  getNextResponsePage,
  getQueryInfoErr,
  getQuerySessionsErr,
  loadQueriesFailure,
  newQuery,
  newQueryFailure,
  queries as queriesAction,
  queriesEnter,
  queriesLeave,
  query as queryAction,
  queryActions,
  queryExports,
  response as responseAction,
  responseNextPage,
  runScanQuery,
  sqlMetadata,
  updateQueryFailure,
} from './queries.actions';
import { canceledExports, currentQuery, currentResponseResultByCursorId } from './queries.selectors';

@Injectable()
export class QueryEffects {
  constructor(
    private actions$: Actions,
    private store: Store<State>,
    private actionService: ClusterActionService,
    private queryService: QueryService,
    private exportQueryService: ExportQueryService,
    private wsService: DashboardApiService,
    private confirm: Confirm,
  ) {}

  // API call errors

  getQueryInfoError$ = createEffect(() =>
    interceptActionTaskErrors(this.queryService, 'getQueryInfo').pipe(
      map((error) =>
        getQueryInfoErr({
          error: extractError(error, 'Failed to load query info'),
        }),
      ),
    ),
  );

  getQuerySessionsError$ = createEffect(() =>
    interceptActionTaskErrors(this.queryService, 'querySessions').pipe(
      map((error) =>
        getQuerySessionsErr({
          error: extractError(error, 'Failed to load query sessions'),
        }),
      ),
    ),
  );

  //

  initLoading$ = createEffect(() =>
    this.actions$.pipe(
      handleLoading([
        {
          start: executeQuery,
          end: [executeQueryFailure, responseAction, createQuery3xSuccess, executeQuery3xFailure],
        },
        { start: executeExportQuery, end: [executeExportQuerySuccess, executeExportQueryFailure] },
        {
          start: executePartialQuery,
          end: [executeQueryFailure, responseAction, createQuery3xSuccess, executeQuery3xFailure],
        },
        { start: executeExportPartialQuery, end: [executeExportQuerySuccess, executeExportQueryFailure] },
        { start: runScanQuery, end: [queryAction, newQueryFailure] },
        { start: cancelQuery, end: [cancelQuerySuccess, cancelQueryFailure] },
      ]),
    ),
  );

  visitPage$ = createEffect(() =>
    combineLatest([this.actions$.pipe(ofType(ROUTER_NAVIGATION)), this.store.select(currentCluster)]).pipe(
      withLatestFrom(this.store.select(selectUrl)),
      map(([[e, clusterId], url]) => !!url.match(`clusters/${clusterId}/sql`)),
      startWith(false),
      pairwise(),
      filter((pair): pair is [false, true] => {
        const [prev, cur] = pair;
        return cur && !prev;
      }),
      map(() => queriesEnter()),
    ),
  );

  leavePage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_NAVIGATED),
      withLatestFrom(this.store.select(currentCluster), this.store.select(selectUrl)),
      map(([e, clusterId, url]) => !!url.match(`clusters/${clusterId}/sql`)),
      pairwise(),
      filter((pair): pair is [true, false] => {
        const [prev, cur] = pair;
        return !cur && !!prev;
      }),
      map(() => queriesLeave()),
    ),
  );

  createQuery$ = createEffect(() =>
    this.actions$.pipe(
      ofType(newQuery),
      pluck('data'),
      mergeMap((data) =>
        this.queryService.create(data).pipe(
          withLatestFrom(this.store.select(dashboardGroupByName(QUERIES_GROUP_NAME))),
          switchMap(([, group]) =>
            group
              ? of(
                  queryAction({ data }),
                  updateDashboardGroup({ name: group.name, ids: [...group.workspaceIds, data.id] }),
                )
              : EMPTY,
          ),
          catchError((error) =>
            of(
              newQueryFailure({
                error: extractError(error, `Can't create Query`),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  changeCurrentQueryParameters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(changeCurrentQueryParameters),
      pluck('parameters'),
      withLatestFrom(this.store.select(currentQuery)),
      switchMap(([parameters, curQuery]) => {
        if (!curQuery) return EMPTY;
        if (curQuery.type !== QueryType.SQL) throw new Error(`Can't change parameters for non sql query`);
        return of(queryAction({ data: { ...curQuery, parameters } }));
      }),
    ),
  );

  updateQuery$ = createEffect(() =>
    this.actions$.pipe(
      ofType(queryAction),
      pluck('data'),
      // Do not add debounce here
      switchMap((query) =>
        this.queryService.update(query).pipe(
          switchMap(() => EMPTY),
          catchError((error) =>
            of(
              updateQueryFailure({
                error: extractError(error, `Can't update query`),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  cancelCurrentQuery$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cancelCurrentQuery),
      withLatestFrom(this.store.select(currentQuery)),
      switchMap(([, query]) =>
        query
          ? this.confirm
              .confirm({
                title: `Cancel the query?`,
                message: `This action will cancel the query, but all completed operations will not be reverted.`,
                dismissButtonLabel: `No`,
                confirmButtonLabel: `Yes`,
              })
              .pipe(switchMap((value) => (value ? [cancelQuery({ id: query.id })] : EMPTY)))
          : EMPTY,
      ),
    ),
  );

  cancelQuery$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cancelQuery),
      pluck('id'),
      withLatestFrom(this.store.select(currentCluster)),
      mergeMap(([id, clusterId]) =>
        !clusterId
          ? EMPTY
          : this.actionService.dispatch(ActionName.QUERY_CANCEL, { clusterId, argument: { queryId: id } }).pipe(
              filter(
                (
                  response,
                ): response is ClusterActionCompletedResponse<ActionName.QUERY_CANCEL> | ClusterActionFailedResponse =>
                  response.status !== ResponseStatus.RUNNING,
              ),
              map((response) =>
                response.status === ResponseStatus.COMPLETED
                  ? cancelQuerySuccess({ id })
                  : cancelQueryFailure({
                      error: extractError(response.error, `Can't cancel query`),
                    }),
              ),
            ),
      ),
    ),
  );

  clearQueryResult$ = createEffect(() =>
    this.actions$.pipe(
      ofType(executeQuery, executePartialQuery, executeExportQuery, executeExportPartialQuery),
      withLatestFrom(this.store.select(currentQuery)),
      map(([, query]) => query?.id),
      filter(Boolean),
      map((id) => clearQueryResult({ id })),
    ),
  );

  handleExecuteQueryError = (e: any) => {
    const error = e.code === GmcErrorCodes.INVALID_QUERY_PARAMETER ? `Empty parameter: ${e.parameter}` : e.toString();
    return of(executeQueryFailure({ error }));
  };

  handleExecuteExportQueryError = (e: any) => {
    const error = e.code === GmcErrorCodes.INVALID_QUERY_PARAMETER ? `Empty parameter: ${e.parameter}` : e.toString();
    return of(executeExportQueryFailure({ error }));
  };

  executeQuery$ = createEffect(() =>
    this.actions$.pipe(
      ofType(executeQuery),
      withLatestFrom(
        this.store.select(currentQuery),
        this.store.select(currentCluster),
        this.store.select(currentClusterNodes),
        this.store.select(isCluster2xSelector),
      ),
      mergeMap(([, query, clusterId, nodes, is2x]) => {
        if (!clusterId || !query || !is2x) return EMPTY;
        let dispatch;
        switch (query.type) {
          case QueryType.SQL:
            try {
              const args = prepareExecuteSqlArgs(query, clusterId, nodes);
              dispatch = this.actionService.dispatch(ActionName.SQL_QUERY_EXECUTE, args);
            } catch (e: any) {
              return this.handleExecuteQueryError(e);
            }
            break;
          case QueryType.SCAN:
            try {
              const args = prepareExecuteScanQueryArgs(query, clusterId, nodes);
              dispatch = this.actionService.dispatch(ActionName.SCAN_QUERY_EXECUTE, args);
            } catch (e: any) {
              return this.handleExecuteQueryError(e);
            }
            break;
          default:
            return throwError(() => 'Unknown query type');
        }

        return dispatch.pipe(
          catchError(() => EMPTY),
          map(
            (
              // TODO: CC-907 (ex: GG-28292)
              response: ExecuteQueryResponseType<ActionName.SCAN_QUERY_EXECUTE | ActionName.SQL_QUERY_EXECUTE>,
            ) => {
              let titles: string[];
              if (query.type === QueryType.SQL) {
                const queryWithValues = replaceFrontendParametersWithValues(query.query, query.parameters);
                titles = extractStatementsFromQuery(queryWithValues);
              } else titles = ['Result'];
              return responseAction({
                response: 'job' in response ? response.job : response,
                queryId: query.id,
                queryType: query.type,
                titles,
              });
            },
          ),
          takeUntil(
            this.actions$.pipe(
              ofType(cancelQuerySuccess),
              filter((action) => action.id === query.id),
            ),
          ),
        );
      }),
    ),
  );

  executeExportQuery$ = createEffect(() =>
    this.actions$.pipe(
      ofType(executeExportQuery),
      withLatestFrom(
        this.store.select(currentQuery),
        this.store.select(currentCluster),
        this.store.select(currentClusterNodes),
      ),
      switchMap(([, query, clusterId, nodes]) => {
        if (!clusterId || !query) return EMPTY;
        try {
          switch (query.type) {
            case QueryType.SQL:
              if (query?.query !== undefined) {
                const args = prepareExecuteSqlArgs(query, clusterId, nodes);
                this.exportQueryService.export(ActionName.SQL_QUERY_EXECUTE, args);
              }
              break;
            case QueryType.SCAN:
              const args = prepareExecuteScanQueryArgs(query, clusterId, nodes);
              this.exportQueryService.export(ActionName.SCAN_QUERY_EXECUTE, args);
              break;
            default:
              return throwError(() => 'Unknown query type');
          }
        } catch (e: any) {
          return this.handleExecuteExportQueryError(e);
        }
        return EMPTY;
      }),
    ),
  );

  executePartialQuery$ = createEffect(() =>
    this.actions$.pipe(
      ofType(executePartialQuery),
      withLatestFrom(
        this.store.select(currentQuery),
        this.store.select(currentCluster),
        this.store.select(currentClusterNodes),
        this.store.select(isCluster2xSelector),
      ),
      mergeMap(([param, query, clusterId, nodes, is2x]) => {
        if (!clusterId || !query || !isSqlQuery(query) || !is2x) return EMPTY;
        let dispatch;
        try {
          const args = prepareExecuteSqlArgs({ ...query, query: param.query }, clusterId, nodes);
          dispatch = this.actionService.dispatch(ActionName.SQL_QUERY_EXECUTE, args);
        } catch (e: any) {
          return this.handleExecuteQueryError(e);
        }
        return dispatch.pipe(
          catchError(() => EMPTY),
          map(
            (
              // TODO: CC-907 (ex: GG-28292)f
              response: ExecuteQueryResponseType<ActionName.SQL_QUERY_EXECUTE>,
            ) => {
              const queryWithValues = replaceFrontendParametersWithValues(param.query, query.parameters);
              const titles = extractStatementsFromQuery(queryWithValues);
              return responseAction({
                response: 'job' in response ? response.job : response,
                queryId: query.id,
                queryType: query.type,
                titles,
              });
            },
          ),
          takeUntil(
            this.actions$.pipe(
              ofType(cancelQuerySuccess),
              filter((action) => action.id === query.id),
            ),
          ),
        );
      }),
    ),
  );

  executeExportPartialQuery$ = createEffect(() =>
    this.actions$.pipe(
      ofType(executeExportPartialQuery),
      withLatestFrom(
        this.store.select(currentQuery),
        this.store.select(currentCluster),
        this.store.select(currentClusterNodes),
      ),
      switchMap(([param, query, clusterId, nodes]) => {
        if (!clusterId || !query || !isSqlQuery(query)) return EMPTY;
        try {
          const args = prepareExecuteSqlArgs({ ...query, query: param.query }, clusterId, nodes);
          this.exportQueryService.export(ActionName.SQL_QUERY_EXECUTE, args);
        } catch (e: any) {
          return this.handleExecuteExportQueryError(e);
        }
        return EMPTY;
      }),
    ),
  );

  queryActions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(queriesEnter),
      withLatestFrom(this.store.select(currentCluster)),
      switchMap(([, clusterId]) =>
        !clusterId
          ? EMPTY
          : merge(
              ...[ResponseStatus.COMPLETED, ResponseStatus.FAILED, ResponseStatus.CANCELED, ResponseStatus.RUNNING].map(
                (status) =>
                  this.actionService.getActionsByStatus(clusterId, status).pipe(
                    map((actions) =>
                      actions.filter(
                        (a): a is ClusterAction<ActionName.SQL_QUERY_EXECUTE | ActionName.SCAN_QUERY_EXECUTE> =>
                          [ActionName.SQL_QUERY_EXECUTE, ActionName.SCAN_QUERY_EXECUTE].includes(a.action),
                      ),
                    ),
                    filter((actions) => !!actions.length),
                    map((actions) => queryActions({ actions, status })),
                    takeUntil(this.actions$.pipe(ofType(queriesLeave))),
                  ),
              ),
            ),
      ),
    ),
  );

  queryExports$ = createEffect(() =>
    this.actions$.pipe(
      ofType(queriesEnter),
      withLatestFrom(this.store.select(currentCluster)),
      switchMap(([, clusterId]) =>
        !clusterId
          ? EMPTY
          : this.exportQueryService.getExportStatuses(clusterId).pipe(
              map((exports) => queryExports({ exports })),
              takeUntil(this.actions$.pipe(ofType(queriesLeave))),
            ),
      ),
    ),
  );

  // allows to handle responses initiated out of current session scope (no receipts in service)
  getResponseOnCurrentQueryChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(queryActions),
      filter((actions) => actions.status === ResponseStatus.RUNNING),
      withLatestFrom(this.store.select(currentCluster)),
      switchMap(([action, clusterId]) =>
        !clusterId
          ? EMPTY
          : merge(
              ...action.actions.map((a) =>
                this.actionService
                  .getResponse<ActionName.SQL_QUERY_EXECUTE | ActionName.SCAN_QUERY_EXECUTE>(a.id, clusterId, a.id)
                  .pipe(
                    take(1),
                    catchError((response) => of(response)),
                    map((r) => r[0]),
                    map((response) => {
                      let titles: string[];
                      let queryType: QueryType | undefined = undefined;
                      if ('queryText' in a.argument) {
                        const queryWithValues = replaceActionParametersWithValues(
                          a.argument.queryText,
                          a.argument.parameters,
                        );
                        titles = extractStatementsFromQuery(queryWithValues);
                        queryType = QueryType.SQL;
                      } else {
                        titles = ['Result'];
                        queryType = QueryType.SCAN;
                      }
                      return responseAction({
                        response,
                        queryId: a.argument.queryId,
                        queryType,
                        titles,
                      });
                    }),
                    takeUntil(
                      this.actions$.pipe(
                        ofType(cancelQuerySuccess),
                        filter((action) => action.id === a.argument.queryId),
                      ),
                    ),
                  ),
              ),
            ),
      ),
    ),
  );

  finishedExportsHandling$ = createEffect(() =>
    this.actions$.pipe(
      ofType(queryExports),
      pluck('exports'),
      filter(
        (exports): exports is QueryExportMessage =>
          !Array.isArray(exports) && [ResponseStatus.COMPLETED, ResponseStatus.FAILED].includes(exports.status),
      ),
      withLatestFrom(this.store.select(canceledExports)),
      map(([exportMessage, canceledExports]) => {
        if (canceledExports.find(({ id }) => id === exportMessage.id))
          return cancelExportQuerySuccess({ message: exportMessage });

        const { status, resourceId, errorMsg } = exportMessage;
        return status === ResponseStatus.COMPLETED
          ? executeExportQuerySuccess({
              notification: 'The query result is ready. The download will start shortly',
              resourceId: resourceId as string,
            })
          : executeExportQueryFailure({ error: extractError(`Query export failed: ${errorMsg}`) });
      }),
    ),
  );

  downloadExportResult$ = createEffect(() =>
    this.actions$.pipe(
      ofType(executeExportQuerySuccess),
      pluck('resourceId'),
      switchMap((resourceId) =>
        this.exportQueryService.downloadResult(resourceId).pipe(
          switchMap(() => EMPTY),
          catchError((err) =>
            of(
              downloadExportQueryFailure({
                error: extractError(err, `Failed to download query export result`),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  getResponseRows$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getNextResponsePage),
      pluck('cursorId'),
      mergeMap((cursorId) =>
        of(cursorId).pipe(
          withLatestFrom(
            this.store.select(currentQuery),
            this.store.select(currentCluster),
            this.store.select(currentResponseResultByCursorId(cursorId)),
          ),
          switchMap(([cursorId, query, clusterId, curResult]) => {
            if (!curResult || !query || !clusterId) return EMPTY;

            const queryId = query.id;
            const argument = {
              cursorId,
              queryId,
              pageSize: SQL_RESPONSE_PAGE_SIZE,
            };

            return this.actionService.dispatch(ActionName.QUERY_NEXT_PAGE, { clusterId, argument }).pipe(
              completedClusterAction(),
              map((response) => responseNextPage({ cursorId, response: response.result, queryId })),
              catchError((response) =>
                of(
                  getNextPageFailure({
                    error: extractError(response, `Can't load more rows`),
                    queryId,
                    cursorId,
                  }),
                ),
              ),
            );
          }),
        ),
      ),
    ),
  );

  loadSqlMetadata$ = createEffect(() =>
    this.actions$.pipe(
      ofType(queriesEnter),
      switchMap(() =>
        this.actions$.pipe(
          ofType(caches),
          map((it) => it.clusterId),
          distinctUntilChanged(),
          switchMap((clusterId) =>
            this.queryService.getSqlMetadata(clusterId).pipe(map((data) => sqlMetadata({ data, clusterId }))),
          ),
          takeUntil(this.actions$.pipe(ofType(queriesLeave))),
        ),
      ),
    ),
  );

  loadSqlMetadataOnEnter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(queriesEnter),
      switchMap(() =>
        this.store.select(clusterIdsOfLoadedCaches).pipe(
          distinctUntilChanged((a, b) => isEqual(a, b)),
          switchMap((clusterIds) =>
            !clusterIds.length
              ? EMPTY
              : merge(
                  ...clusterIds.map((id) =>
                    this.queryService.getSqlMetadata(id).pipe(map((data) => sqlMetadata({ data, clusterId: id }))),
                  ),
                ),
          ),
          takeUntil(this.actions$.pipe(ofType(queriesLeave))),
        ),
      ),
    ),
  );

  queriesDashboardGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(queriesEnter),
      switchMap(() =>
        this.wsService.subscribeToWorkspaceGroups().pipe(
          switchMap((groups) =>
            // we get all groups at once from the topic subscription
            merge(
              ...groups
                .filter((group) => group.name === QUERIES_GROUP_NAME) // filter out the relevant group
                .map((group) =>
                  merge(
                    of(dashboardGroup({ dashboardGroup: group })),
                    // get all queries for the group
                    merge(...group.workspaceIds.map((id) => this.queryService.get(id))).pipe(
                      toArray(),
                      map((list) => queriesAction({ list })),
                      catchError((error) => [
                        loadQueriesFailure({
                          error: extractError(error, `Can't get queries`),
                        }),
                      ]),
                    ),
                  ),
                ),
            ),
          ),
          takeUntil(this.actions$.pipe(ofType(queriesLeave))),
        ),
      ),
    ),
  );

  changeCurrentQueryAdvancedSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(changeCurrentQueryAdvancedSettings),
      pluck('settings'),
      withLatestFrom(this.store.select(currentQuery)),
      switchMap(([settings, curQuery]) =>
        !curQuery ? EMPTY : of(queryAction({ data: { ...curQuery, ...settings } })),
      ),
    ),
  );
}
